import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { get, set } from 'idb-keyval';
import { Company } from '../interfaces/company.interface';
import { filter, first, map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class SelectedCompanyService {
	private readonly url: string = 'SelectCompany';
	/** Current company id */
	private companyId$: BehaviorSubject<number> = new BehaviorSubject(null);
	private companies$: BehaviorSubject<Company[]> = new BehaviorSubject<Company[]>(null);

	public initializeCompanies$ = this.companyId$.pipe(
		first(cid => cid !== null),
		switchMap(companyId => this.getCompanies().pipe(map((cas) => {
			this.companies$.next(cas);

			if(!cas.some(c => c.id === companyId) && cas.length > 0) {
				companyId = cas[0].id;
			}

			return this.setCompanyIdImmediate(companyId);
		}))
	), shareReplay());

	constructor(private httpClient: HttpClient) {
		get('selectedCompany').then((result: { companyId: number } | number) => {
			if (result && typeof result === 'object' && 'companyId' in result) {
				this.companyId$.next(result.companyId);
			}
			if (result && typeof result === 'number') {
				this.companyId$.next(result);
			} else {
				this.companyId$.next(-1); // use this as null value for stupid historical reasons
			}
		});
	}

	/** Get the companyID */
	public getSelectedCompanyID(): number {
		const companyId = this.companyId$.value;
		if (!companyId) {
			return -1;
		}
		return companyId;
	}

	public getCompanyIdObservable(): Observable<number> {
		return this.companyId$.pipe(filter(cid => cid !== null));
	}

	public getCompaniesObservable(): Observable<Company[]> {
		return this.initializeCompanies$.pipe(switchMap(() => this.companies$));
	}

	public async setCompanyId(companyId: number): Promise<boolean> {
		await firstValueFrom(this.initializeCompanies$);
		return this.setCompanyIdImmediate(companyId);
	}

	private setCompanyIdImmediate(companyId: number): boolean {
		if(companyId === -1) {
			this.companyId$.next(companyId);
			return true;
		}

		const companyAssociation = this.companies$.value.find(uca => uca.id === companyId);
		if(companyAssociation === null) return false;

		return this.setCompanyIdDirectly(companyId);
	}

	public setCompanyIdDirectly(companyId: number) {
		this.companyId$.next(companyId);
		set('selectedCompany', companyId).then(r => {});
		return true;
	}

	public getSelectedCompany(): Observable<Company> {
		return this.httpClient.get<Company>(`${this.url}/getSelectedCompany`);
	}

	public getCompanies(): Observable<Company[]> {
		return this.httpClient.get<Company[]>(`${this.url}/getCompanies`);
	}
}
